import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  activatePromptVersion,
  createPromptVersion,
  getPromptTemplate,
  getPromptTemplateVersion,
  listPromptTemplates,
  previewPrompt,
  type CreatePromptVersionRequest,
  type PromptKind,
  type PromptPreviewRequest,
  type PromptPreviewResultResponse,
  type PromptTemplateDetailResponse,
  type PromptTemplateSummaryResponse,
  type PromptTemplateVersionResponse,
} from '../api';

export function usePromptTemplates(): UseQueryResult<PromptTemplateSummaryResponse[]> {
  return useQuery({
    queryKey: AdminQueryKeys.prompts(),
    queryFn: ({ signal }) => listPromptTemplates(signal),
    staleTime: 30_000,
  });
}

export function usePromptTemplate(kind: PromptKind): UseQueryResult<PromptTemplateDetailResponse> {
  return useQuery({
    queryKey: AdminQueryKeys.promptDetail(kind),
    queryFn: ({ signal }) => getPromptTemplate(kind, signal),
    staleTime: 30_000,
  });
}

export function usePromptTemplateVersion(
  kind: PromptKind,
  versionId: string | null,
): UseQueryResult<PromptTemplateVersionResponse> {
  return useQuery({
    queryKey: AdminQueryKeys.promptVersion(kind, versionId ?? ''),
    queryFn: ({ signal }) =>
      versionId !== null ? getPromptTemplateVersion(kind, versionId, signal) : Promise.reject(new Error('versionId is required.')),
    enabled: versionId !== null,
    staleTime: 60_000,
  });
}

export function useCreatePromptVersion(kind: PromptKind) {
  const qc = useQueryClient();
  return useMutation<PromptTemplateVersionResponse, Error, CreatePromptVersionRequest>({
    mutationFn: (req) => createPromptVersion(kind, req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.prompts() });
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.promptDetail(kind) });
    },
  });
}

export function useActivatePromptVersion(kind: PromptKind) {
  const qc = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: (versionId) => activatePromptVersion(kind, versionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.prompts() });
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.promptDetail(kind) });
    },
  });
}

export function usePreviewPrompt(kind: PromptKind) {
  return useMutation<PromptPreviewResultResponse, Error, PromptPreviewRequest>({
    mutationFn: (req) => previewPrompt(kind, req),
  });
}
